import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { handleTimezoneLocale } from "../i18n";
import { currencies } from "./CurrencyListWithSymbols";
import { getLanguage, isObjectEmpty } from "./utils";

dayjs.extend(utc);
dayjs.extend(timezone);

// const timeZone = require('dayjs-timezone');
export const defaultCurrency = process.env.REACT_APP_DEFAULT_CURRENCY;
export const defaultDateFormat = process.env.REACT_APP_DEFAULT_DATE_FORMAT;
export const defaultNumberFormat = process.env.REACT_APP_DEFAULT_NUMBER_FORMAT;
export const defaultPhoneNumber =
  process.env.REACT_APP_DEFAULT_PHONE_NUMBER_FORMAT;
export const defaultLanguage = process.env.REACT_APP_DEFAULT_LANGUAGE;
export const defaultTimeZone = process.env.REACT_APP_DEFAULT_TIME_ZONE;

export const getSettings = (settings) => {
  let settingObject = {};
  settings.length > 0 &&
    settings.forEach((item) => {
      if (item) {
        settingObject = {
          ...settingObject,
          [item.setting_name]: item.setting_value,
        };
      }
    });
  return settingObject;
};

/***RETURN USER DATE FORMAT ACCORDING TO USER TIMEZONE***/
export const getDateByUserFormat = (
  settings,
  value = "",
  defaultValue = "",
  nonUtc = false
) => {
  if (!isObjectEmpty(value)) {
    /** Set Date Format Variable **/
    const dateFormat =
      settings?.date_format !== "" && settings?.date_format !== null
        ? settings?.date_format || defaultDateFormat
        : defaultDateFormat;

    /**** SET USER LANGUAGE ****/
    let language = "en";
    if (settings?.language) {
      if (settings.language) {
        language = getLanguage({ language: settings.language });
      }
    }
    handleTimezoneLocale(language);
    if (dayjs.locale() !== language) {
      dayjs.locale(language);
    }
    /***********************/
    if (nonUtc) {
      return dayjs(value).format(dateFormat);
    } else {
      const convertedDate = getDateByUserTimeZone(
        settings,
        value,
        defaultValue
      );
      //  console.log("value:", value, "convertedDate::", convertedDate);
      return dayjs.utc(convertedDate).format(dateFormat);
    }
  } else {
    return defaultValue;
  }
};
/***RETURN DATE TIME FORMAT ACCORDING TO USER TIMEZONE***/
export const getDateByUserTimeZone = (
  settings,
  value = "",
  defaultValue = "",
  format12hour = false
) => {
  if (!isObjectEmpty(value)) {
    const dateFormat = format12hour
      ? "YYYY-MM-DD hh:mm A"
      : "YYYY-MM-DD HH:mm:ss";
    const userTimeZone = settings?.time_zone || defaultTimeZone;

    let language = "en";
    if (settings && settings.language) {
      if (settings.language) {
        language = getLanguage({ language: settings.language });
      }
    }
    handleTimezoneLocale(language); // Handle language-specific timezone formatting (if needed)
    if (dayjs.locale() !== language) {
      dayjs.locale(language);
    }

    const isoFormats = {
      am: "YYYY-MM-DD HH:mm a",
      pm: "YYYY-MM-DD HH:mm a",
      AM: "YYYY-MM-DD HH:mm A",
      PM: "YYYY-MM-DD HH:mm A",
    };

    // Parse the date based on potential format and handle invalid formats
    let parsedDate;
    if (dayjs.isDayjs(value)) {
      value = value.format("YYYY-MM-DD HH:mm:ss");
      parsedDate = dayjs.utc(value);
    } else if (value instanceof Date) {
      parsedDate = dayjs.utc(value);
    } else if (
      isoFormats[Object.keys(isoFormats).find((item) => value.includes(item))]
    ) {
      parsedDate = dayjs(
        value,
        isoFormats[Object.keys(isoFormats).find((item) => value.includes(item))]
      );
    } else {
      parsedDate = dayjs.utc(value); // ISO string or fallback
    }

    const utcDate = parsedDate.utc(true);

    const convertedDate = utcDate.tz(userTimeZone);
    return convertedDate.format(dateFormat);
  } else {
    return defaultValue;
  }
};

export const getDateByUserTimeZone1 = (
  settings,
  value = "",
  defaultValue = "",
  format12hour = false
) => {
  if (!value) return defaultValue;

  const dateFormat = format12hour
    ? "YYYY-MM-DD hh:mm A"
    : "YYYY-MM-DD HH:mm:ss";
  const userTimeZone = settings?.time_zone || defaultTimeZone;

  if (settings?.language) {
    const language = getLanguage({ language: settings.language });
    handleTimezoneLocale(language);
    if (dayjs.locale() !== language) dayjs.locale(language);
  }

  let parsedDate;
  if (dayjs.isDayjs(value)) {
    parsedDate = dayjs.utc(value);
  } else if (value instanceof Date) {
    parsedDate = dayjs.utc(value);
  } else {
    parsedDate = dayjs.utc(value); // ISO string or fallback
  }

  if (!parsedDate.isValid()) return defaultValue;

  const convertedDate = parsedDate.utc().tz(userTimeZone);

  return convertedDate.format(dateFormat);
};

/*** RETURN ONLY DATE FORMAT ACCORDING TO USER TIMEZONE ***/
export const getDateFormatOnly = (settings, value = "", defaultValue = "") => {
  if (value !== "" && value !== null && value !== undefined) {
    /** Set Date Format Variable **/
    let dateFormat = settings?.date_format || defaultDateFormat;
    dateFormat = dateFormat.replace(" h:mm:ss A", "");

    if (dateFormat.lastIndexOf(",") > -1) {
      dateFormat = dateFormat.slice(0, dateFormat.length - 1);
    }
    const convertedDate = getDateByUserTimeZone(settings, value, defaultValue);
    /**** SET USER LANGUAGE ****/

    let language = "en";
    if (settings && settings.language) {
      if (settings.language) {
        language = getLanguage({ language: settings.language });
      }
    }

    handleTimezoneLocale(language);
    if (dayjs.locale() !== language) {
      dayjs.locale(language);
    }

    /*****/
    return dayjs.utc(convertedDate).format(dateFormat);
  } else {
    return defaultValue;
  }
};

/*** RETURN ONLY DATE FORMAT ACCORDING TO USER DATE ***/
export const getDateWithoutTimezoneChange = (
  settings,
  value = "",
  defaultValue = ""
) => {
  if (value === "" || value === null || value === undefined)
    return defaultValue;

  /** Set Date Format Variable **/
  let dateFormat = settings?.date_format || defaultDateFormat;

  // Remove time part from format (keep only date tokens)
  dateFormat = dateFormat.replace(" h:mm:ss A", "");
  if (dateFormat.lastIndexOf(",") > -1) {
    dateFormat = dateFormat.slice(0, dateFormat.length - 1);
  }

  /**** SET USER LANGUAGE ****/
  let language = "en";
  if (settings?.language) {
    language = getLanguage({ language: settings.language });
  }

  handleTimezoneLocale(language);
  if (dayjs.locale() !== language) {
    dayjs.locale(language);
  }

  /**
   * IMPORTANT:
   * To keep the date the same (no timezone shifting),
   * extract the date portion if an ISO datetime is provided.
   */
  let dateOnlyValue = value;
  if (typeof value === "string") {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/); // grabs YYYY-MM-DD from ISO-like strings
    if (match) dateOnlyValue = match[1];
  }

  return dayjs(dateOnlyValue).format(dateFormat);
};

export const getNumberByUserFormat = (
  settings,
  value = 0,
  type = "number",
  defaultValue = "0"
) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return defaultValue;

  const numberFormat = settings?.number_format ?? defaultNumberFormat;

  const formatted = (() => {
    if (numberFormat === "1,00,000") {
      return num.toFixed(2).replace(/(\d)(?=(\d\d)+\d\.)/g, "$1,");
    }

    if (numberFormat === "1 00 000") {
      return num.toFixed(2).replace(/(\d)(?=(\d\d)+\d\.)/g, "$1 ");
    }

    return num.toFixed(2);
  })();

  const [intPart, decimal] = formatted.split(".");
  const result = Number(decimal) > 0 ? formatted : intPart;

  return type === "currency"
    ? `${getCurrencyByUserFormat(settings)}${result}`
    : result;
};

/**
 *
 * @param {*} settings
 * @param {*} value
 * @param {*} defaultValue
 * @returns formatted phone/mobile number in user defined format...
 */
export const getPhoneByUserFormat = (
  settings,
  value = "",
  defaultValue = ""
) => {
  if (value !== "" && value !== null && value !== undefined) {
    const userPhoneFormat = settings?.phone_number_format || defaultPhoneNumber;
    if (userPhoneFormat === "(123) 234-5689") {
      // const match = value.match(/(\d{3})(\d{3})(\d{4})$/);
      let myRegex = /^(\d{0,4})(\d{3})(\d{3})(\d{4,7})$/;
      value = value.replace(/\D/g, "");
      const match = value.match(myRegex);

      if (match) {
        return (
          "+" + match[1] + " (" + match[2] + ") " + match[3] + "-" + match[4]
        );
      }
    } else if (userPhoneFormat === "123-4567890") {
      value = value.replace(/\D/g, "");
      // const match = value.match(/(\d{3})(\d{7})$/);
      const match = value.match(/^(\d{0,4})(\d{3})(\d{7})$/);
      if (match) {
        return "+" + match[1] + " " + match[2] + "-" + match[3];
      }
    } else if (userPhoneFormat === "1234567890") {
      return value;
    } else {
      return value;
    }
  } else {
    return defaultValue;
  }
  return "+" + value;
};

export const getCurrencyByUserFormat = (settings) => {
  let currency = defaultCurrency;
  if (settings && settings.currency) {
    currency = settings.currency;
  }
  return (
    currencies &&
    currencies.filter(
      (item) => item.code.toLowerCase() === currency.toLowerCase()
    )[0].symbol
  );
};
